/**
 * Petites fonctions de présentation partagées par les écrans.
 *
 * Elles ne décident de rien : elles mettent en forme des valeurs que le serveur a déjà validées
 * (hash, dates, identifiants) et masquent ce qui ne doit jamais s'afficher en clair.
 */
import { M } from '../catalogue/messages';

import type { Element, Rarete } from '@elemora/shared/content';

/** Formate une date ISO en date et heure françaises (`19/10/2026 08:56`). */
export function formaterDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

/**
 * Tronque un hash pour l'affichage, en gardant assez de caractères pour le distinguer.
 *
 * Le hash complet reste disponible dans l'attribut `title` : un administrateur qui compare deux
 * versions doit pouvoir lire la valeur entière au survol.
 */
export function hashCourt(hash: string): string {
  return hash.length <= 16 ? hash : `${hash.slice(0, 16)}…`;
}

/** Libellé français d'une rareté de carte. */
export function libelleRarete(rarete: Rarete): string {
  return M.raretes[rarete];
}

/** Libellé français d'un élément (la carte peut être `neutre`, la DA ne l'est jamais). */
export function libelleElement(element: Element | 'neutre'): string {
  return M.elements[element];
}

/** Libellé français d'une action du journal d'audit, ou l'action brute si elle est inconnue. */
export function libelleAction(action: string): string {
  const connue = M.audit.actions[action as keyof typeof M.audit.actions];
  return connue ?? M.audit.actionInconnue(action);
}

/** Motif d'adresse électronique, volontairement large : mieux vaut masquer trop que trop peu. */
const MOTIF_EMAIL = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

/**
 * Masque toute adresse électronique d'un texte.
 *
 * Le serveur masque déjà les emails de l'audit (DECISIONS §11) ; cette seconde passe est une
 * ceinture de sécurité côté affichage : si un champ libre en contenait un, il ne s'afficherait
 * pas en clair dans le back-office.
 */
export function masquerEmails(texte: string): string {
  return texte.replace(MOTIF_EMAIL, (adresse) => {
    const [local = '', domaine = ''] = adresse.split('@');
    return `${local.slice(0, 1)}***@${domaine}`;
  });
}

/** Sérialise une valeur d'audit (`avant` / `apres`) de façon lisible, emails masqués. */
export function jsonLisible(valeur: unknown): string {
  if (valeur === null || valeur === undefined) return M.audit.aucunDetail;
  try {
    return masquerEmails(JSON.stringify(valeur, null, 2));
  } catch {
    return M.audit.aucunDetail;
  }
}
